import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeMount, ref, shallowRef, watch, useSlots, withDefaults } from 'vue';
import { useRoute } from 'vue-router/composables';
import SvgActions from '@/assets/icons/actions.svg';
import BaseCheckbox from '@/components-UI/BaseCheckbox.vue';
import BaseLineLoader from '@/components-UI/BaseLineLoader.vue';
import BasePagination from '@/components-UI/table/BasePagination.vue';
import TopScroll from '@/components-UI/table/TopScroll.vue';
import BaseQuestionMarkTooltip from '@/components-UI/BaseQuestionMarkTooltip.vue';
import TableFooterButtons from '@/components/base/TableUI/TableFooterButtons.vue';
import SortableHead from '@/components-UI/table/SortableHead.vue';
import store from '@/store';
import { PAGINATION_PER_PAGE } from '@/configs/global';
import { updateQueryString } from '@/utils/updateQueryString';
import { useCalculateTextWidth } from '@/utils/calculateTextWidth';
import { CellsComponentsMap } from '@/utils/CellsFromBff';
import type { ITableFooterButton, ITableTimeData } from '@/types';
import type { FormattedItem } from '@/utils/CellsFactory';
import type { ITableHeader } from '@/utils';

/********************
 * Props & Emits
 *******************/
interface Props {
    headers: ITableHeader[];
    items: FormattedItem<any>[] | any[];
    selected?: any[];
    showSelect?: boolean;
    uniqueData?: any[];
    timeData?: { [key: string]: ITableTimeData };
    addRowClass?: (item?: any) => string;
    isHideNoDataIcon?: boolean;
    hideNoResultBlock?: boolean;
    noDataText?: string;
    noResultText?: string;
    sectionHeader?: boolean;
    loading?: boolean;
    topScroll?: boolean;
    // TODO isShowPagination нужен до тех пор пока не искореним AppTableDesign, после удалить
    isShowPagination?: boolean;
    isFrontPagination?: boolean | string; // <- Добавил для добавления своего ключа в query
    total?: number;
    offset?: number;
    itemsPerPage?: number;
    isShowSelect?: boolean;
    footerButtons?: ITableFooterButton[];
    isEditMode?: boolean;
    isResizableCols?: boolean;
    syncColumnsWithQuery?: boolean; // отвязать колонки от query
    sortColumnQueryKey?: string;
    sortDirectionQueryKey?: string;
    serverSideSort?: boolean;
    pageSizes?: number[];
    defaultPageSize?: number;
    // Скрыть колонки - нужно указать value у headers
    hiddenColumns?: string[];
}

interface ISection { section: string; description?: string; style?: string; colspan: number; key: string }


export default /*#__PURE__*/_defineComponent({
  __name: 'BaseTable',
  props: {
    headers: { default: () => [] },
    items: { default: () => [] },
    selected: { default: () => [] },
    showSelect: { type: Boolean },
    uniqueData: null,
    timeData: null,
    addRowClass: null,
    isHideNoDataIcon: { type: Boolean },
    hideNoResultBlock: { type: Boolean },
    noDataText: { default: 'Ничего не найдено' },
    noResultText: { default: 'Ничего не найдено' },
    sectionHeader: { type: Boolean },
    loading: { type: Boolean },
    topScroll: { type: Boolean },
    isShowPagination: { type: Boolean },
    isFrontPagination: { type: [Boolean, String] },
    total: null,
    offset: null,
    itemsPerPage: { default: PAGINATION_PER_PAGE },
    isShowSelect: { type: Boolean },
    footerButtons: null,
    isEditMode: { type: Boolean },
    isResizableCols: { type: Boolean },
    syncColumnsWithQuery: { type: Boolean, default: true },
    sortColumnQueryKey: { default: 'sort_by' },
    sortDirectionQueryKey: { default: 'sort_dir' },
    serverSideSort: { type: Boolean, default: false },
    pageSizes: null,
    defaultPageSize: null,
    hiddenColumns: null
  },
  emits: ["update:selected", "update:options", "change-page", "change-limit"],
  setup(__props: any, { expose, emit }: { emit: ({
    (e: 'update:selected', value: any[]): void;
    (e: 'update:options', value: any): void;
    (e: 'change-page', offset: number): void;
    (e: 'change-limit', { offset: number, limit: number })
}), expose: any, slots: any, attrs: any }) {

const props = __props as { headers: ITableHeader[], items: FormattedItem<any>[] | any[], selected: any[], showSelect?: boolean, uniqueData?: any[], timeData?: { [key: string]: ITableTimeData }, addRowClass?: (item?: any) => string, isHideNoDataIcon?: boolean, hideNoResultBlock?: boolean, noDataText: string, noResultText: string, sectionHeader?: boolean, loading?: boolean, topScroll?: boolean, isShowPagination?: boolean, isFrontPagination?: boolean | string, total?: number, offset?: number, itemsPerPage: number, isShowSelect?: boolean, footerButtons?: ITableFooterButton[], isEditMode?: boolean, isResizableCols?: boolean, syncColumnsWithQuery: boolean, sortColumnQueryKey: string, sortDirectionQueryKey: string, serverSideSort: boolean, pageSizes?: number[], defaultPageSize?: number, hiddenColumns?: string[] }





/********************
 * Constants & Refs
 *******************/
const ROW_DEFAULT_CLASS = 'base-table__body-tr';
const tableComp = ref<HTMLTableElement | null>(null);
const tableWrap = ref<HTMLDivElement | null>(null);
const route = useRoute();
const slots = useSlots();

const widthMap = shallowRef(new Map<string, string>());
const selectedSet = shallowRef<Set<any>>(new Set(props.selected));

/********************
 * Utils
 *******************/
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

const toArray = <T,>(val: T | T[]) => (Array.isArray(val) ? val : [val]);

const updateQuery = (q: Record<string, any>) => {
    const current = route.query as Record<string, any>;
    updateQueryString({ ...current, ...q });
};

const getOffsetKey = computed<string>(() => {
    return typeof props.isFrontPagination === 'string' ? props.isFrontPagination : 'offset';
});

/********************
 * Headers & Visibility
 *******************/
const baseHeaders = ref<ITableHeader[]>([]);

onBeforeMount(() => {
    baseHeaders.value = props.headers.map((h) => ({ ...h, width: h.width ?? '100px' }));
    baseHeaders.value.forEach((h) => widthMap.value.set(h.value, h.width!));
    initSortFromQuery();
    initPageFromQuery();
});

watch(() => props.headers, (newHeaders) => {
    baseHeaders.value = newHeaders.map((h) => ({ ...h, width: h.width ?? '100px' }));
    const map = new Map(widthMap.value);
    baseHeaders.value.forEach((h) => { if (!map.has(h.value)) map.set(h.value, h.width!); });
    widthMap.value = map;
});

const visibleHeaders = computed<ITableHeader[]>(() => {

    if (!props.syncColumnsWithQuery) {
        return baseHeaders.value.map((h) => ({ ...h, width: widthMap.value.get(h.value) || h.width || '100px' }));
    }

    const qCols = route.query.columns;
    const selected = qCols ? new Set(toArray(qCols as any)) : null;

    return baseHeaders.value.filter((h) => {
        if (props.isEditMode && h.isShowOnlyOnEdit) return true;
        if (h.fixed) return true;
        if (!selected) return true;
        return selected.has(h.value as any);
    }).map((h) => ({ ...h, width: widthMap.value.get(h.value) || h.width || '100px' }));
});

const noDataRowColspan = computed(() => {
    const dataCols = visibleHeaders.value.filter(
        (h) => !props.hiddenColumns?.includes(h.value),
    ).length;
    return (props.showSelect ? 1 : 0) + dataCols;
});

/********************
 * Sorting
 *******************/
const sortBy = ref('');
const sortDir = ref<'asc' | 'desc' | ''>('');

function changeSort(sortByVal: string) {
    const colKey = props.sortColumnQueryKey;
    const dirKey = props.sortDirectionQueryKey;

    if (sortByVal !== sortBy.value) {
        sortBy.value = sortByVal;
        sortDir.value = 'desc';
        updateQuery({
            [colKey]: sortBy.value,
            [dirKey]: sortDir.value,
        });
        return;
    }

    switch (sortDir.value) {
    case 'desc':
        sortDir.value = 'asc';
        sortBy.value = sortByVal;
        break;
    case '':
        sortDir.value = 'desc';
        sortBy.value = sortByVal;
        break;
    case 'asc':
        sortDir.value = '';
        sortBy.value = '';
        break;
    }
    updateQuery({
        [colKey]: sortBy.value || undefined,
        [dirKey]: sortDir.value || undefined,
    });
}

function initSortFromQuery() {
    const rawB = route.query[props.sortColumnQueryKey];
    const rawD = route.query[props.sortDirectionQueryKey];
    const qb = (Array.isArray(rawB) ? rawB[0] : rawB) as string | undefined;
    const qdRaw = (Array.isArray(rawD) ? rawD[0] : rawD) as string | undefined;
    const qd = qdRaw === 'asc' || qdRaw === 'desc' ? qdRaw : undefined;
    if (qb && qd) {
        sortBy.value = qb;
        sortDir.value = qd;
    } else {
        sortBy.value = '';
        sortDir.value = '';
    }
}

watch(
    () => [
        route.query[props.sortColumnQueryKey],
        route.query[props.sortDirectionQueryKey],
    ],
    () => {
        initSortFromQuery();
    },
);

function cellComparableValue(row: any, key: string) {
    const cell = row?.[key];
    const cd = cell?.componentData;
    let value: any = cd?.value ?? cell;
    if (Array.isArray(value)) value = value.join('');
    if (cell?.type === 'status') value = value?.text ?? '';
    if (value == null) return '';
    if (typeof value === 'number') return value;
    return String(value);
}

const sortedAndPagedItems = computed(() => {
    const src = props.items as any[];
    let out = src;

    if (!props.serverSideSort && sortBy.value) {
        const key = sortBy.value;
        const order = sortDir.value === 'asc' ? 1 : -1;

        out = src
            .map((item, idx) => ({ item, idx, val: cellComparableValue(item, key) }))
            .sort((a, b) => {
                const av = a.val, bv = b.val;
                let cmp = 0;
                if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
                else cmp = collator.compare(String(av), String(bv));
                if (cmp === 0) cmp = a.idx - b.idx;
                return cmp * order;
            })
            .map((x) => x.item);
    }

    if (props.isFrontPagination) {
        const start = currentOffset.value;
        const perPage = Math.max(limitItems.value, 1);
        return out.slice(start, start + perPage);
    }
    return out;
});

/********************
 * Pagination
 *******************/
const page = ref(1);
const limitItems = ref(props.itemsPerPage);
const currentOffset = computed(() => (page.value - 1) * limitItems.value);

function updatePage(newPage: number) {
    page.value = newPage;
    scrollToTop();
    if (props.isFrontPagination) {
        updateQuery({ [getOffsetKey.value]: currentOffset.value || undefined });
    } else {
        emit('change-page', currentOffset.value);
    }
}

const updateLimit = (limit: number) => {
    limitItems.value = limit;
    if (props.total < currentOffset.value) {
        page.value = Math.ceil(props.total / limit);
    }
    emit('change-limit', { offset: currentOffset.value, limit });
};

function initPageFromQuery() {
    const qOff = Number(route.query[getOffsetKey.value] ?? props.offset ?? 0);
    page.value = Math.floor(qOff / limitItems.value) + 1;
}

watch(() => props.offset, (off) => {
    if (off == null) return;
    page.value = Math.floor(off / limitItems.value) + 1;
    scrollToTop();
});

watch(() => route.query[getOffsetKey.value], (off) => {
    const val = Number(off || 0);
    page.value = Math.floor(val / limitItems.value) + 1;
    scrollToTop();
});

watch(() => props.itemsPerPage, (value) => {
    limitItems.value = value;
});

/********************
 * TopScroll
 *******************/
const isShowTopScroll = computed(() => !props.loading && props.topScroll && props.items.length);

/********************
 * Scrolling
 *******************/
function scrollToTop() {
    const wrap = tableWrap.value;
    if (!wrap) return;
    wrap.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
}

/********************
 * Selection
 *******************/
watch(() => props.selected, (nv) => {
    selectedSet.value = new Set(nv ?? []);
});

const selectableCount = computed(() => (props.items as any[]).filter((r) => r.isSelectable !== false).length);
const selectedCount = computed(() => selectedSet.value.size);
const isAllItemsSelected = computed(() => props.items.length > 0 && selectedSet.value.size === selectableCount.value);
const isNotNullSelectedItems = computed(() => selectedSet.value.size > 0 && selectedSet.value.size < selectableCount.value);

function toggleSelectAll(v: boolean) {
    if (v) {
        selectedSet.value = new Set((props.items as any[]).filter((r) => r.isSelectable !== false));
    } else {
        selectedSet.value = new Set();
    }
    emit('update:selected', Array.from(selectedSet.value));
}

function toggleSelection(item: any, v: boolean) {
    const set = new Set(selectedSet.value);
    if (v) set.add(item); else set.delete(item);
    selectedSet.value = set;
    emit('update:selected', Array.from(selectedSet.value));
}

/********************
 * Column resize (Pointer Events, no global listeners)
 *******************/
function effectiveWidth(h: ITableHeader) {
    return widthMap.value.get(h.value) || h.width || '100px';
}
const startResize = (e: MouseEvent, idx: number) => {
    const th = (e.target as HTMLElement).closest("th") as HTMLElement | null;
    if (!th) return;

    const startX = e.clientX;
    const startWidth = th.offsetWidth;

    const onMove = (ev: MouseEvent) => {
        const newWidth = Math.max(60, startWidth + (ev.clientX - startX));
        th.style.width = `${newWidth}px`;

        const isLast = idx === visibleHeaders.value.length - 1;
        const isScroll = newWidth > startWidth;

        if (isLast && isScroll) {
            const wrap = tableWrap?.value as HTMLElement;
            wrap.scrollTo({ top: wrap.scrollTop, left: wrap.scrollLeft + newWidth - startWidth });
        }
    };

    const onUp = (ev: MouseEvent) => {
        const newWidth = Math.max(60, startWidth + (ev.clientX - startX));
        const hdr = visibleHeaders.value[idx];
        if (!hdr) return;
        const map = new Map(widthMap.value);
        map.set(hdr.value, `${newWidth}px`);
        widthMap.value = map;

        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
};

/********************
 * Sections (single pass aggregation)
 *******************/
const sections = computed<ISection[]>(() => {
    const hs = visibleHeaders.value;
    if (!hs.length) return [];

    const counts = new Map<string, number>();
    const defs = hs.map((h) => ({
        section: h.section || '',
        description: h.sectionDescription,
        colspan: 1,
        style: '',
        key: `sect-${h.value}`,
    }));

    for (const s of defs) counts.set(s.section, (counts.get(s.section) || 0) + 1);

    const result: ISection[] = [];
    const seen = new Set<string>();
    for (const s of defs) {
        if (s.section === '') { result.push(s); continue; }
        if (seen.has(s.section)) continue;
        seen.add(s.section);
        const count = counts.get(s.section) || 1;
        s.colspan = count;
        s.style = `width: ${count === 1 ? `${useCalculateTextWidth(s.section, { font: 'Inter, sans-serif', fontSize: 10, letterSpacing: 0.28 })}px` : 'auto'};`;
        result.push(s);
    }

    if (props.showSelect) result.unshift({ section: '', colspan: 1, key: '___select', style: '' });
    return result;
});

/********************
 * Misc
 *******************/
const wrapClasses = computed(() => ({
    '--no-shadow': props.sectionHeader,
    '--is-footer-content': slots.tfoot,
}));

function resolveComponent(cell: any) {
    return cell && typeof cell === 'object' && 'componentData' in cell ? CellsComponentsMap[cell.type] : null;
}

function addDivider(index: number) {
    const cur = visibleHeaders.value[index];
    const next = visibleHeaders.value[index + 1];
    return (next && cur.section !== next.section);
}

function rowClass(item: any) { return `${ROW_DEFAULT_CLASS} ${props.addRowClass?.(item) ?? ''}`; }

function getNestedValue(obj: any, path: string) {
    const parts = path.split('.');
    let v: any = obj;
    for (const p of parts) v = v?.[p];
    if (v && typeof v === 'object') {
        const values = Object.values(v);
        if (values.some((x) => x == null)) return '';
        return v;
    }
    return v ?? '';
}

/********************
 * Filters state check
 *******************/
const isFiltered = computed(() => {
    const filters = store.state.filtersParamObject;
    const defaults = store.state.defaultFiltersParamObject;
    const a = JSON.stringify(filters);
    const b = JSON.stringify(defaults);
    return a.length !== b.length || a !== b;
});

/********************
 * Expose
 *******************/
expose({ scrollToTop });

return { __sfc: true,props, emit, ROW_DEFAULT_CLASS, tableComp, tableWrap, route, slots, widthMap, selectedSet, collator, toArray, updateQuery, getOffsetKey, baseHeaders, visibleHeaders, noDataRowColspan, sortBy, sortDir, changeSort, initSortFromQuery, cellComparableValue, sortedAndPagedItems, page, limitItems, currentOffset, updatePage, updateLimit, initPageFromQuery, isShowTopScroll, scrollToTop, selectableCount, selectedCount, isAllItemsSelected, isNotNullSelectedItems, toggleSelectAll, toggleSelection, effectiveWidth, startResize, sections, wrapClasses, resolveComponent, addDivider, rowClass, getNestedValue, isFiltered, SvgActions, BaseCheckbox, BaseLineLoader, BasePagination, TopScroll, BaseQuestionMarkTooltip, TableFooterButtons, SortableHead }
}

})